import type {
  AppointmentPlanningPreviewPayload,
  AppointmentPlanningPreviewResponse,
  AppointmentPlanningPreviewRow,
} from "../../../../../../api/kehrblatt/planning"

/** Replan only requested rows; include the remaining draft as fixed planning context. */
export const buildScopedPlanningPreviewPayload = (
  payload: AppointmentPlanningPreviewPayload | null,
  draftRows: AppointmentPlanningPreviewRow[],
  targetIds: ReadonlySet<string>,
): AppointmentPlanningPreviewPayload | null => {
  if (!payload) return null

  const drafts = new Map(draftRows.map((row) => [row.client_row_id, row]))

  return {
    ...payload,
    employee_ids: Array.from(
      new Set([
        ...payload.employee_ids,
        ...draftRows
          .filter((row) => !targetIds.has(row.client_row_id) && row.commit_target?.kind === "SET")
          .map((row) => row.appointment?.employee_id ?? "")
          .filter(Boolean),
      ]),
    ),
    rows: payload.rows.map((row) => {
      const draft = drafts.get(row.client_row_id)
      const target = draft?.commit_target

      if (targetIds.has(row.client_row_id) || !target) return row

      if (target.kind === "KEEP_EXISTING")
        return { ...row, planning_strategy: "KEEP_EXISTING" as const }

      return {
        ...row,
        planning_strategy: "MANUAL" as const,
        appointment_employee_id: target.appointment.employee_id,
        manual_appointment_type:
          target.appointment.flex_type === "SOFT" ? ("FLEX" as const) : ("HARD" as const),
        manual_appointment: { ...target.appointment },
      }
    }),
  }
}

/** Never replace untouched drafts with the planner's response for their context rows. */
export const mergeScopedPlanningPreview = (
  previous: AppointmentPlanningPreviewResponse | null,
  response: AppointmentPlanningPreviewResponse,
  targetIds: ReadonlySet<string>,
): AppointmentPlanningPreviewResponse => {
  const previousRows = new Map(previous?.rows.map((row) => [row.client_row_id, row]) ?? [])

  return {
    ...response,
    rows: response.rows.map((row) =>
      targetIds.has(row.client_row_id) ? row : (previousRows.get(row.client_row_id) ?? row),
    ),
  }
}
